import {
  NutritionalValue,
  FoodItem,
  DailyNutrition,
  Meal,
  createCalories,
  createGrams,
  createMilligrams,
} from '@/types/nutrition'
import { InvalidNutritionalValueError } from '@/types/parser'

/**
 * Validates that all nutritional values are non-negative.
 *
 * @param nutrition - NutritionalValue to validate
 * @throws InvalidNutritionalValueError When any nutritional value is negative
 *
 * @example
 * ```typescript
 * validateNutritionalValue({
 *   calories: createCalories(178),
 *   carbs: createGrams(16)
 *   // ... other values
 * })
 * ```
 */
export const validateNutritionalValue = (nutrition: NutritionalValue): void => {
  const fields = [
    { name: 'calories', value: nutrition.calories },
    { name: 'carbs', value: nutrition.carbs },
    { name: 'fat', value: nutrition.fat },
    { name: 'protein', value: nutrition.protein },
    { name: 'cholesterol', value: nutrition.cholesterol },
    { name: 'sodium', value: nutrition.sodium },
    { name: 'sugar', value: nutrition.sugar },
    { name: 'fiber', value: nutrition.fiber },
  ]

  for (const field of fields) {
    if (field.value < 0) {
      throw new InvalidNutritionalValueError(field.value.toString(), field.name, undefined)
    }
  }
}

/**
 * Validates a food item's structure and data integrity.
 *
 * @param foodItem - FoodItem to validate
 * @throws Error When food item has invalid name, quantity, or nutrition values
 *
 * @example
 * ```typescript
 * validateFoodItem({
 *   name: 'Apple',
 *   quantity: { amount: 110, unit: 'gram' },
 *   nutrition: { calories: createCalories(57), ... }
 * })
 * ```
 */
export const validateFoodItem = (foodItem: FoodItem): void => {
  if (!foodItem.name || foodItem.name.trim() === '') {
    throw new Error('Food name cannot be empty')
  }

  if (foodItem.quantity.amount <= 0) {
    throw new Error('Quantity amount must be positive')
  }

  validateNutritionalValue(foodItem.nutrition)
}

/**
 * Validates a complete daily nutrition record.
 *
 * @param dailyNutrition - DailyNutrition to validate
 * @throws Error When date is invalid, in the future, or food items are invalid
 *
 * @example
 * ```typescript
 * validateDailyNutrition({
 *   date: new Date('2025-09-01'),
 *   meals: [...],
 *   totals: { calories: createCalories(2000), ... }
 * })
 * ```
 */
export const validateDailyNutrition = (dailyNutrition: DailyNutrition): void => {
  if (isNaN(dailyNutrition.date.getTime())) {
    throw new Error('Invalid date')
  }

  const now = new Date()
  now.setHours(23, 59, 59, 999) // End of today
  if (dailyNutrition.date > now) {
    throw new Error('Date cannot be in the future')
  }

  for (const meal of dailyNutrition.meals) {
    for (const food of meal.foods) {
      validateFoodItem(food)
    }
  }

  validateNutritionalValue(dailyNutrition.totals)
}

/**
 * Calculates total nutritional values from all meals.
 *
 * @param meals - Array of meals to sum up
 * @returns NutritionalValue with calculated totals
 *
 * @example
 * ```typescript
 * const totals = calculateTotals([
 *   { category: 'Breakfast', foods: [...] },
 *   { category: 'Lunch', foods: [...] }
 * ])
 * // Returns: NutritionalValue with summed values
 * ```
 */
export const calculateTotals = (meals: readonly Meal[]): NutritionalValue => {
  let totalCalories = 0
  let totalCarbs = 0
  let totalFat = 0
  let totalProtein = 0
  let totalCholesterol = 0
  let totalSodium = 0
  let totalSugar = 0
  let totalFiber = 0

  for (const meal of meals) {
    for (const food of meal.foods) {
      totalCalories += food.nutrition.calories
      totalCarbs += food.nutrition.carbs
      totalFat += food.nutrition.fat
      totalProtein += food.nutrition.protein
      totalCholesterol += food.nutrition.cholesterol
      totalSodium += food.nutrition.sodium
      totalSugar += food.nutrition.sugar
      totalFiber += food.nutrition.fiber
    }
  }

  return {
    calories: createCalories(totalCalories),
    carbs: createGrams(totalCarbs),
    fat: createGrams(totalFat),
    protein: createGrams(totalProtein),
    cholesterol: createMilligrams(totalCholesterol),
    sodium: createMilligrams(totalSodium),
    sugar: createGrams(totalSugar),
    fiber: createGrams(totalFiber),
  }
}

/**
 * Validates that provided totals match calculated totals within tolerance.
 *
 * @param meals - Array of meals to calculate totals from
 * @param providedTotals - Totals provided in the data
 * @param tolerancePercent - Acceptable percentage difference (e.g., 1.0 for 1%)
 * @returns True if totals match within tolerance, false otherwise
 *
 * @example
 * ```typescript
 * const isValid = validateTotals(meals, providedTotals, 1.0)
 * // Returns: true if calculated and provided totals are within 1%
 * ```
 */
export const validateTotals = (
  meals: readonly Meal[],
  providedTotals: NutritionalValue,
  tolerancePercent: number
): boolean => {
  const calculatedTotals = calculateTotals(meals)

  const fields = [
    { calculated: calculatedTotals.calories, provided: providedTotals.calories },
    { calculated: calculatedTotals.carbs, provided: providedTotals.carbs },
    { calculated: calculatedTotals.fat, provided: providedTotals.fat },
    { calculated: calculatedTotals.protein, provided: providedTotals.protein },
    { calculated: calculatedTotals.cholesterol, provided: providedTotals.cholesterol },
    { calculated: calculatedTotals.sodium, provided: providedTotals.sodium },
    { calculated: calculatedTotals.sugar, provided: providedTotals.sugar },
    { calculated: calculatedTotals.fiber, provided: providedTotals.fiber },
  ]

  for (const field of fields) {
    const tolerance = Math.abs(field.calculated * (tolerancePercent / 100))
    const difference = Math.abs(field.calculated - field.provided)

    if (difference > tolerance) {
      return false
    }
  }

  return true
}
